import {
  BiddingSpecConfig,
  DeckSpecConfig,
  FeatureFlags,
  GameTableConfig,
  PhaseConfig,
  PlayRulesConfig,
  ScoringSpecConfig,
  ServerGameTableConfig,
  TimingConfig,
  UiHints,
} from './types';
import { TableConfigOption } from '../../games/common/IGameRules';
import {
  DEFAULT_TIMING,
  GENERIC_BIDDING,
  GENERIC_DECK_CONFIG,
  GENERIC_DEFAULT_CONFIG,
  GENERIC_FEATURES,
  GENERIC_PHASES,
  GENERIC_PLAY_RULES,
  GENERIC_SCORING,
  GENERIC_UI_HINTS,
} from './defaults';
import { GAME_UI_PRESENTATION } from './presentation';

/**
 * Reads a value supporting camelCase or PascalCase key from source, with a fallback.
 */
export function getValue<T>(source: Record<string, any>, camelCase: string, pascalCase: string, fallback: T): T {
  return (source[camelCase] ?? source[pascalCase] ?? fallback) as T;
}

/**
 * Coerces numeric value from source supporting camelCase or PascalCase.
 */
export function getNumber(source: Record<string, any>, camelCase: string, pascalCase: string, fallback: number): number {
  const val = source[camelCase] ?? source[pascalCase];
  return val != null ? Number(val) : fallback;
}

/**
 * Coerces boolean value from source supporting camelCase or PascalCase.
 */
export function getBoolean(source: Record<string, any>, camelCase: string, pascalCase: string, fallback: boolean): boolean {
  const val = source[camelCase] ?? source[pascalCase];
  return val != null ? Boolean(val) : fallback;
}

/**
 * Coerces string value from source supporting camelCase or PascalCase.
 */
export function getString(source: Record<string, any>, camelCase: string, pascalCase: string, fallback: string): string {
  const val = source[camelCase] ?? source[pascalCase];
  return val != null ? String(val) : fallback;
}

/**
 * Normalizes phases array supporting both camelCase and PascalCase phase items.
 */
export function normalizePhases(rawPhases: any, fallbackPhases: PhaseConfig[]): PhaseConfig[] {
  if (!Array.isArray(rawPhases) || rawPhases.length === 0) {
    return fallbackPhases;
  }
  return rawPhases.map((p: any) => ({
    id: getString(p, 'id', 'Id', 'Unknown'),
    timeoutSeconds: getNumber(p, 'timeoutSeconds', 'TimeoutSeconds', 30),
    isTurnBased: getBoolean(p, 'isTurnBased', 'IsTurnBased', false),
  }));
}

/**
 * Normalizes table options array supporting both camelCase and PascalCase items.
 */
export function normalizeTableOptions(rawOptions: any, fallbackOptions: TableConfigOption[]): TableConfigOption[] {
  if (!Array.isArray(rawOptions) || rawOptions.length === 0) {
    return fallbackOptions;
  }
  return rawOptions.map((opt: any): TableConfigOption => {
    const type = getString(opt, 'type', 'Type', 'select').toLowerCase();
    if (type === 'checkbox') {
      return {
        id: getString(opt, 'id', 'Id', ''),
        label: getString(opt, 'label', 'Label', ''),
        type: 'checkbox',
        defaultValue: getBoolean(opt, 'defaultValue', 'DefaultValue', false),
        description: getString(opt, 'description', 'Description', ''),
      };
    }
    return {
      id: getString(opt, 'id', 'Id', ''),
      label: getString(opt, 'label', 'Label', ''),
      type: 'select',
      defaultValue: String(getValue(opt, 'defaultValue', 'DefaultValue', '')),
      options: Array.isArray(opt.options ?? opt.Options)
        ? (opt.options ?? opt.Options).map((o: any) => ({
            value: String(getValue(o, 'value', 'Value', '')),
            label: getString(o, 'label', 'Label', ''),
          }))
        : [],
    };
  });
}

/**
 * Authoritative normalizer for server-provided GameTableConfig.
 * 
 * Architectural principles:
 * 1. The .NET server is the authoritative source for gameplay rules, scoring, bidding, legal play, deck, and phases.
 * 2. If the config cannot be obtained from the server, returns null (no client-side rule fallbacks).
 * 3. Client owns presentation concerns (HUD visibility, score display modes, display terminology).
 * 4. Case-tolerant (supports camelCase or PascalCase from .NET serialization).
 */
export function normalizeGameTableConfig(raw: ServerGameTableConfig | any): GameTableConfig | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const rawFeatures = raw.features ?? raw.Features;
  const rawDeck = raw.deck ?? raw.Deck;
  const rawBidding = raw.bidding ?? raw.Bidding;
  const rawScoring = raw.scoring ?? raw.Scoring;
  const rawPlayRules = raw.playRules ?? raw.PlayRules;

  // Authoritative server config must provide at least one core rule section
  if (!rawFeatures && !rawDeck && !rawBidding && !rawScoring && !rawPlayRules) {
    return null;
  }

  const rawTypeStr = raw.gameType ?? raw.GameType ?? '';
  const detectedType = rawTypeStr.toString().trim().toUpperCase();

  const baseConfig: GameTableConfig = GENERIC_DEFAULT_CONFIG;

  // Presentation defaults for the detected game
  const uiPresentationDefaults: UiHints = (detectedType && GAME_UI_PRESENTATION[detectedType])
    ? GAME_UI_PRESENTATION[detectedType]
    : GENERIC_UI_HINTS;

  const rawUi = raw.ui ?? raw.Ui ?? {};
  const rawTiming = raw.timing ?? raw.Timing ?? {};
  const rawPhases = raw.phases ?? raw.Phases;
  const rawTableOptions = raw.tableOptions ?? raw.TableOptions;

  // 1. Presentation UI hints (Client-owned presentation)
  const ui: UiHints = {
    scoreDisplayMode: getValue(rawUi, 'scoreDisplayMode', 'ScoreDisplayMode', uiPresentationDefaults.scoreDisplayMode),
    scoreMode: getString(rawUi, 'scoreMode', 'ScoreMode', uiPresentationDefaults.scoreMode || 'TEAMS'),
    badgeMode: getString(rawUi, 'badgeMode', 'BadgeMode', uiPresentationDefaults.badgeMode || 'BOOKS_WON'),
    themeSymbol: getString(rawUi, 'themeSymbol', 'ThemeSymbol', uiPresentationDefaults.themeSymbol || '♠'),
    showContractHud: getBoolean(rawUi, 'showContractHud', 'ShowContractHud', uiPresentationDefaults.showContractHud),
    showBooksToSetCounter: getBoolean(rawUi, 'showBooksToSetCounter', 'ShowBooksToSetCounter', uiPresentationDefaults.showBooksToSetCounter),
    showBagsCounter: getBoolean(rawUi, 'showBagsCounter', 'ShowBagsCounter', uiPresentationDefaults.showBagsCounter),
    showDirectionIndicator: getBoolean(rawUi, 'showDirectionIndicator', 'ShowDirectionIndicator', uiPresentationDefaults.showDirectionIndicator),
    targetScore: getNumber(rawUi, 'targetScore', 'TargetScore', uiPresentationDefaults.targetScore),
    trickNoun: getString(rawUi, 'trickNoun', 'TrickNoun', uiPresentationDefaults.trickNoun ?? 'trick'),
    trickNounPlural: getString(rawUi, 'trickNounPlural', 'TrickNounPlural', uiPresentationDefaults.trickNounPlural ?? 'tricks'),
    terminology: getValue(rawUi, 'terminology', 'Terminology', uiPresentationDefaults.terminology ?? {}),
  };

  // 2. Timing (Server-authoritative timeouts + visual animation defaults)
  const timing: TimingConfig = {
    turnTimeoutSec: getNumber(rawTiming, 'turnTimeoutSec', 'TurnTimeoutSec', baseConfig.timing.turnTimeoutSec),
    biddingTimeoutSec: getNumber(rawTiming, 'biddingTimeoutSec', 'BiddingTimeoutSec', baseConfig.timing.biddingTimeoutSec),
    trumpDeclarationTimeoutSec: getNumber(rawTiming, 'trumpDeclarationTimeoutSec', 'TrumpDeclarationTimeoutSec', baseConfig.timing.trumpDeclarationTimeoutSec),
    kittyDiscardTimeoutSec: getNumber(rawTiming, 'kittyDiscardTimeoutSec', 'KittyDiscardTimeoutSec', baseConfig.timing.kittyDiscardTimeoutSec),
    passingTimeoutSec: getNumber(rawTiming, 'passingTimeoutSec', 'PassingTimeoutSec', baseConfig.timing.passingTimeoutSec ?? 20),
    dealAnimationMs: getNumber(rawTiming, 'dealAnimationMs', 'DealAnimationMs', baseConfig.timing.dealAnimationMs),
    trickSweepDelayMs: getNumber(rawTiming, 'trickSweepDelayMs', 'TrickSweepDelayMs', baseConfig.timing.trickSweepDelayMs),
    handRevealDelayMs: getNumber(rawTiming, 'handRevealDelayMs', 'HandRevealDelayMs', baseConfig.timing.handRevealDelayMs),
    readyCountdownSec: getNumber(rawTiming, 'readyCountdownSec', 'ReadyCountdownSec', baseConfig.timing.readyCountdownSec),
    botBiddingDelayMs: getNumber(rawTiming, 'botBiddingDelayMs', 'BotBiddingDelayMs', baseConfig.timing.botBiddingDelayMs),
    botPlayDelayMs: getNumber(rawTiming, 'botPlayDelayMs', 'BotPlayDelayMs', baseConfig.timing.botPlayDelayMs),
    botTrumpDelayMs: getNumber(rawTiming, 'botTrumpDelayMs', 'BotTrumpDelayMs', baseConfig.timing.botTrumpDelayMs),
  };

  // 3. Deck Specification (Server-authoritative)
  const deck: DeckSpecConfig = {
    totalCards: getNumber(rawDeck || {}, 'totalCards', 'TotalCards', baseConfig.deck.totalCards),
    includeJokers: getBoolean(rawDeck || {}, 'includeJokers', 'IncludeJokers', baseConfig.deck.includeJokers),
    cardsPerPlayer: getNumber(rawDeck || {}, 'cardsPerPlayer', 'CardsPerPlayer', baseConfig.deck.cardsPerPlayer),
    kittyCards: getNumber(rawDeck || {}, 'kittyCards', 'KittyCards', baseConfig.deck.kittyCards),
    deckProfile: getString(rawDeck || {}, 'deckProfile', 'DeckProfile', baseConfig.deck.deckProfile || 'STANDARD'),
  };

  // 4. Feature Flags (Server-authoritative)
  const features: FeatureFlags = {
    hasKitty: getBoolean(rawFeatures || {}, 'hasKitty', 'HasKitty', baseConfig.features.hasKitty),
    hasTrumpDeclaration: getBoolean(rawFeatures || {}, 'hasTrumpDeclaration', 'HasTrumpDeclaration', baseConfig.features.hasTrumpDeclaration),
    hasKittyDiscard: getBoolean(rawFeatures || {}, 'hasKittyDiscard', 'HasKittyDiscard', baseConfig.features.hasKittyDiscard),
    hasDirectionBidding: getBoolean(rawFeatures || {}, 'hasDirectionBidding', 'HasDirectionBidding', baseConfig.features.hasDirectionBidding),
    hasBrokenTrumpRule: getBoolean(rawFeatures || {}, 'hasBrokenTrumpRule', 'HasBrokenTrumpRule', baseConfig.features.hasBrokenTrumpRule),
    hasBagPenalty: getBoolean(rawFeatures || {}, 'hasBagPenalty', 'HasBagPenalty', baseConfig.features.hasBagPenalty),
    hasClaimMechanic: getBoolean(rawFeatures || {}, 'hasClaimMechanic', 'HasClaimMechanic', baseConfig.features.hasClaimMechanic),
    canBoston: getBoolean(rawFeatures || {}, 'canBoston', 'CanBoston', baseConfig.features.canBoston),
    hasSingleContractBidder: getBoolean(rawFeatures || {}, 'hasSingleContractBidder', 'HasSingleContractBidder', baseConfig.features.hasSingleContractBidder),
    hasNoTrumpOption: getBoolean(rawFeatures || {}, 'hasNoTrumpOption', 'HasNoTrumpOption', baseConfig.features.hasNoTrumpOption),
    totalTricksPerHand: getNumber(rawFeatures || {}, 'totalTricksPerHand', 'TotalTricksPerHand', baseConfig.features.totalTricksPerHand),
    totalBooksPerHand: (rawFeatures || {}).totalBooksPerHand ?? (rawFeatures || {}).TotalBooksPerHand ?? baseConfig.features.totalBooksPerHand,
    hasPassingPhase: getBoolean(rawFeatures || {}, 'hasPassingPhase', 'HasPassingPhase', Boolean(baseConfig.features.hasPassingPhase)),
    hasTeams: getBoolean(rawFeatures || {}, 'hasTeams', 'HasTeams', baseConfig.features.hasTeams ?? true),
    trackPenaltyPoints: getBoolean(rawFeatures || {}, 'trackPenaltyPoints', 'TrackPenaltyPoints', Boolean(baseConfig.features.trackPenaltyPoints)),
    canEndHandEarlyOnSet: getBoolean(rawFeatures || {}, 'canEndHandEarlyOnSet', 'CanEndHandEarlyOnSet', Boolean(baseConfig.features.canEndHandEarlyOnSet)),
    defaultTrumpSuit: getValue(rawFeatures || {}, 'defaultTrumpSuit', 'DefaultTrumpSuit', baseConfig.features.defaultTrumpSuit),
    hasBidding: getBoolean(rawFeatures || {}, 'hasBidding', 'HasBidding', Boolean(baseConfig.features.hasBidding)),
  };

  // 5. Bidding Specification (Server-authoritative)
  const rawAllowedBidTypes = (rawBidding || {}).allowedBidTypes ?? (rawBidding || {}).AllowedBidTypes;
  const bidding: BiddingSpecConfig = {
    biddingModel: getString(rawBidding || {}, 'biddingModel', 'BiddingModel', baseConfig.bidding.biddingModel || 'NONE'),
    minBid: getNumber(rawBidding || {}, 'minBid', 'MinBid', baseConfig.bidding.minBid),
    maxBid: getNumber(rawBidding || {}, 'maxBid', 'MaxBid', baseConfig.bidding.maxBid),
    allowPass: getBoolean(rawBidding || {}, 'allowPass', 'AllowPass', baseConfig.bidding.allowPass),
    stickTheDealer: getBoolean(rawBidding || {}, 'stickTheDealer', 'StickTheDealer', baseConfig.bidding.stickTheDealer),
    stuckDealerMinBid: getNumber(rawBidding || {}, 'stuckDealerMinBid', 'StuckDealerMinBid', baseConfig.bidding.stuckDealerMinBid),
    stuckDealerPassCount: getNumber(rawBidding || {}, 'stuckDealerPassCount', 'StuckDealerPassCount', baseConfig.bidding.stuckDealerPassCount),
    allowedBidTypes: Array.isArray(rawAllowedBidTypes) ? rawAllowedBidTypes : baseConfig.bidding.allowedBidTypes,
    nilBonusPoints: getNumber(rawBidding || {}, 'nilBonusPoints', 'NilBonusPoints', baseConfig.bidding.nilBonusPoints),
    nilPenaltyPoints: getNumber(rawBidding || {}, 'nilPenaltyPoints', 'NilPenaltyPoints', baseConfig.bidding.nilPenaltyPoints),
    allowBlindNil: getBoolean(rawBidding || {}, 'allowBlindNil', 'AllowBlindNil', baseConfig.bidding.allowBlindNil),
    blindNilBonusPoints: getNumber(rawBidding || {}, 'blindNilBonusPoints', 'BlindNilBonusPoints', baseConfig.bidding.blindNilBonusPoints),
  };

  // 6. Scoring Specification (Server-authoritative)
  const scoring: ScoringSpecConfig = {
    model: getString(rawScoring || {}, 'model', 'Model', baseConfig.scoring.model || 'NONE'),
    winCondition: getString(rawScoring || {}, 'winCondition', 'WinCondition', baseConfig.scoring.winCondition || 'HIGHEST_SCORE'),
    targetScore: getNumber(rawScoring || {}, 'targetScore', 'TargetScore', baseConfig.scoring.targetScore),
    settlementType: getString(rawScoring || {}, 'settlementType', 'SettlementType', baseConfig.scoring.settlementType),
    pointsPerContractTrick: getNumber(rawScoring || {}, 'pointsPerContractTrick', 'PointsPerContractTrick', baseConfig.scoring.pointsPerContractTrick),
    pointsPerOvertrick: getNumber(rawScoring || {}, 'pointsPerOvertrick', 'PointsPerOvertrick', baseConfig.scoring.pointsPerOvertrick),
    bagPenaltyThreshold: getNumber(rawScoring || {}, 'bagPenaltyThreshold', 'BagPenaltyThreshold', baseConfig.scoring.bagPenaltyThreshold),
    bagPenaltyPoints: getNumber(rawScoring || {}, 'bagPenaltyPoints', 'BagPenaltyPoints', baseConfig.scoring.bagPenaltyPoints),
    bostonBonusPoints: getNumber(rawScoring || {}, 'bostonBonusPoints', 'BostonBonusPoints', baseConfig.scoring.bostonBonusPoints),
    booksToSetFormula: getString(rawScoring || {}, 'booksToSetFormula', 'BooksToSetFormula', baseConfig.scoring.booksToSetFormula),
    pointCards: getValue(rawScoring || {}, 'pointCards', 'PointCards', baseConfig.scoring.pointCards),
    shootTheMoonPoints: getNumber(rawScoring || {}, 'shootTheMoonPoints', 'ShootTheMoonPoints', baseConfig.scoring.shootTheMoonPoints ?? 0),
    shootTheMoonScoreValue: getNumber(rawScoring || {}, 'shootTheMoonScoreValue', 'ShootTheMoonScoreValue', baseConfig.scoring.shootTheMoonScoreValue ?? 0),
    hasDoubleScoreOnNoTrump: getBoolean(rawScoring || {}, 'hasDoubleScoreOnNoTrump', 'HasDoubleScoreOnNoTrump', Boolean(baseConfig.scoring.hasDoubleScoreOnNoTrump)),
  };

  // 7. Play Rules (Server-authoritative)
  const playRules: PlayRulesConfig = {
    mustFollowLedSuit: getBoolean(rawPlayRules || {}, 'mustFollowLedSuit', 'MustFollowLedSuit', baseConfig.playRules.mustFollowLedSuit),
    trumpLeadRestriction: getValue(rawPlayRules || {}, 'trumpLeadRestriction', 'TrumpLeadRestriction', baseConfig.playRules.trumpLeadRestriction),
    restrictedLeadSuit: getValue(rawPlayRules || {}, 'restrictedLeadSuit', 'RestrictedLeadSuit', baseConfig.playRules.restrictedLeadSuit),
    voidSloughRestriction: getValue(rawPlayRules || {}, 'voidSloughRestriction', 'VoidSloughRestriction', baseConfig.playRules.voidSloughRestriction),
    canTrumpLeadIfOnlyTrumpInHand: getBoolean(rawPlayRules || {}, 'canTrumpLeadIfOnlyTrumpInHand', 'CanTrumpLeadIfOnlyTrumpInHand', baseConfig.playRules.canTrumpLeadIfOnlyTrumpInHand),
    firstTrickLeadCardId: getValue(rawPlayRules || {}, 'firstTrickLeadCardId', 'FirstTrickLeadCardId', baseConfig.playRules.firstTrickLeadCardId),
    preventPointsOnTrick1: getBoolean(rawPlayRules || {}, 'preventPointsOnTrick1', 'PreventPointsOnTrick1', Boolean(baseConfig.playRules.preventPointsOnTrick1)),
    trick1RestrictedCardIds: getValue(rawPlayRules || {}, 'trick1RestrictedCardIds', 'Trick1RestrictedCardIds', baseConfig.playRules.trick1RestrictedCardIds),
    trick1RestrictedSuit: getValue(rawPlayRules || {}, 'trick1RestrictedSuit', 'Trick1RestrictedSuit', baseConfig.playRules.trick1RestrictedSuit),
  };

  // 8. Phases
  const phases: PhaseConfig[] = normalizePhases(rawPhases, baseConfig.phases);

  // 9. Table Options (Server-authoritative legal values and defaults)
  const tableOptions: TableConfigOption[] = normalizeTableOptions(rawTableOptions, baseConfig.tableOptions);

  // 10. Contract Format
  const contractFormat = getString(raw, 'contractFormat', 'ContractFormat', baseConfig.contractFormat);

  return {
    gameType: getString(raw, 'gameType', 'GameType', detectedType || baseConfig.gameType),
    displayName: getString(raw, 'displayName', 'DisplayName', detectedType || baseConfig.displayName),
    deck,
    phases,
    timing,
    features,
    bidding,
    scoring,
    playRules,
    ui,
    contractFormat,
    tableOptions,
  };
}
